<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
    import { faChevronLeft, faChevronRight, faXmark } from "@fortawesome/free-solid-svg-icons";
    import { Input } from "flowbite-svelte";
    
    export let value: { year: number; month: number } | undefined = undefined;
    export let minYear = 1980;
    export let maxYear = new Date().getFullYear();
    export let openAbove = false;
    export let error = false;
    
    const dispatch = createEventDispatcher();
    
    let displayYear = value?.year || new Date().getFullYear();
    let isOpen = false;
    let showDecadeView = false;
    let pickerElement: HTMLElement;
    
    const monthsShort = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    const monthsFull = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    $: decadeStart = Math.floor(displayYear / 10) * 10;
    $: decadeYears = Array.from({ length: 10 }, (_, i) => decadeStart + i);
    $: currentDecade = `${decadeStart}s`;
    
    function togglePicker() {
        if (!isOpen) {
            displayYear = value?.year || new Date().getFullYear();
            showDecadeView = false;
        }
        isOpen = !isOpen;
    }
    
    function selectMonth(monthIndex: number) {
        const newValue = { year: displayYear, month: monthIndex + 1 };
        dispatch('change', newValue);
        value = newValue;
        isOpen = false;
    }

    function selectYear(year: number, event: MouseEvent) {
        event.stopPropagation();  // Stop event from bubbling up
        displayYear = year;
        showDecadeView = false;
    }
    
    function changeYear(delta: number, event: MouseEvent) {
        event.stopPropagation();
        const newYear = showDecadeView ? decadeStart + (delta * 10) : displayYear + delta;
        if (newYear >= minYear && newYear <= maxYear) {
            displayYear = newYear;
        }
    }

    function toggleDecadeView(event: MouseEvent) {
        event.stopPropagation();
        showDecadeView = !showDecadeView;
    }
    
    function formatValue(val: typeof value): string {
        if (!val?.year || !val?.month) return '';
        return `${monthsFull[val.month - 1]} ${val.year}`;
    }
    
    $: getMonthDisabled = (monthIndex: number) => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        
        if (displayYear < currentYear) {
            return false;
        }
        if (displayYear === currentYear) {
            return monthIndex > currentMonth;
        }
        return true;
    };

    $: isYearDisabled = (year: number) => year > maxYear;
    
    $: isPrevYearDisabled = showDecadeView 
        ? decadeStart <= minYear 
        : displayYear <= minYear;
    $: isNextYearDisabled = showDecadeView 
        ? decadeStart + 10 > maxYear 
        : displayYear >= maxYear;
    
    function handleClickOutside(event: MouseEvent) {
        // Get the clicked element
        const target = event.target as HTMLElement;
        
        // Check if the click is inside the picker component
        if (isOpen && pickerElement && !pickerElement.contains(target)) {
            isOpen = false;
            showDecadeView = false;
        }
    }

    function clearValue(event: MouseEvent) {
        event.stopPropagation();
        value = undefined;
        dispatch('change', undefined);
    }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="monthyearpicker-container relative" bind:this={pickerElement}>
    <div class="relative">
        <Input 
            class="app-field cursor-pointer {error ? 'error' : ''}" 
            readonly
            type="text" 
            value={formatValue(value)}
            on:click={togglePicker}
        />
        {#if value}
            <button
                class="clear-button absolute right-2 top-1/2 -translate-y-1/2 p-1"
                on:click={clearValue}
            >
                <FontAwesomeIcon icon={faXmark} />
            </button>
        {/if}
    </div>
   
    {#if isOpen}
        <div 
            class="monthyearpicker-control absolute z-50 border rounded shadow-lg p-1"
            style={openAbove ? 'bottom: calc(100% + 0.125rem)' : 'top: calc(100% + 0.125rem)'}
        >
            <div class="flex justify-between items-center">
                <button 
                    class="previous-button px-2 w-8 h-8 flex items-center justify-center" 
                    class:disabled={isPrevYearDisabled}
                    on:click={(e) => changeYear(-1, e)}
                    disabled={isPrevYearDisabled}
                ><FontAwesomeIcon icon={faChevronLeft} /></button>
                <button 
                    class="view-button text-center"
                    on:click={toggleDecadeView}
                >
                    {showDecadeView ? currentDecade : displayYear}
                </button>
                <button 
                    class="next-button px-2 w-8 h-8 flex items-center justify-center"
                    class:disabled={isNextYearDisabled}
                    on:click={(e) => changeYear(1, e)}
                    disabled={isNextYearDisabled}
                ><FontAwesomeIcon icon={faChevronRight} /></button>
            </div>

            {#if showDecadeView}
                <div class="grid grid-cols-2 h-[200px]">
                    {#each decadeYears as year}
                        {@const isDisabled = isYearDisabled(year)}
                        <button
                            class="year-button flex items-center justify-center"
                            class:selected={year === value?.year}
                            class:disabled={isDisabled}
                            on:click={(e) => selectYear(year, e)}
                            disabled={isDisabled}
                        >
                            {year}
                        </button>
                    {/each}
                </div>
            {:else}
                <div class="grid grid-cols-3 h-[200px]">
                    {#each monthsShort as month, i}
                        {@const isDisabled = getMonthDisabled(i)}
                        <button
                            class="month-button flex items-center justify-center"
                            class:selected={value?.year === displayYear && value?.month === i + 1}
                            class:disabled={isDisabled}
                            on:click={() => selectMonth(i)}
                            disabled={isDisabled}
                        >
                            {month}
                        </button>
                    {/each}
                </div>
            {/if}
        </div>
    {/if}
</div>
